import React from "react";
import { Loader, PolyIcon } from "elements";
import { Link } from "polyrouter";
import * as H from "history";
import { AllQueryParam } from "../../polyrouter/allQueryParams";

/**
 * The sidebar of the page.
 */
class Sidebar extends React.PureComponent<{
  //Do you want the sidebar to stay in its smallest state
  staySmall?: boolean;
}> {
  //Actions go with the sidebar.  Laid out at the bottom.
  static Actions: React.FC = ({ children }) => (
    <div className="actions">{children}</div>
  );

  render() {
    const { children, staySmall, ...rest } = this.props;

    return (
      <div
        className={staySmall ? "sidebar small-sidebar" : "sidebar"}
        {...rest}
      >
        {children}
      </div>
    );
  }
}

/**
 * The main content of the page.
 */
class Content extends React.PureComponent<{
  //Do you expect the content to scroll
  scrolling?: boolean;
  //Is this details content
  details?: boolean;
  //Is there no title? This will push content back down to be even with the sidebar
  noPageTitle?: boolean;
  //Any extra classes for the main content wrapper?
  className?: string;
}> {
  //A title that sits above the main content
  static Title: React.FC<{
    nested?: boolean;
    className?: string;
  }> = ({ children, className, nested }) => {
    const classes = [nested ? "title" : "page-title", className]
      .filter(c => !!c)
      .join(" ");

    return <h1 className={classes}>{children}</h1>;
  };

  //A title that sits above the main content
  static Subheader: React.FC = ({ children }) => (
    <h3 className="sub-header">{children}</h3>
  );

  //scrolling section of repeated main content.
  static ScrollingList: React.FC<{ className?: string; loading?: boolean }> = ({
    loading = false,
    className = "",
    children
  }) => (
    <div className={["scrollable", className].filter(c => !!c).join(" ")}>
      <Loader loading={loading}>{children}</Loader>
    </div>
  );

  render() {
    const {
      scrolling,
      details,
      noPageTitle,
      children,
      className,
      ...rest
    } = this.props;
    const scrollClass = scrolling ? "scroll-content" : "";
    const detailsClass = details ? "details" : "";
    const noPageTitleClass = noPageTitle ? "no-page-title" : "";
    return (
      <div
        className={` ${[
          "main-content",
          scrollClass,
          detailsClass,
          noPageTitleClass,
          className
        ]
          .filter(c => !!c)
          .join(" ")}`}
        {...rest}
      >
        {children}
      </div>
    );
  }
}

const BackButton: React.FC<{
  to: H.LocationDescriptor | ((location: H.Location) => H.LocationDescriptor);
  persist?: Array<AllQueryParam>;
  title: string;
  iconDesc: string;
  linkDataId?: string;
}> = props => {
  const { title, iconDesc, to, persist, linkDataId } = props;
  return (
    <div className="navigate-back">
      <Link persist={persist} to={to} data-testid={linkDataId}>
        <PolyIcon
          className="icon"
          icon="keyboard_arrow_left"
          title={iconDesc}
        />
        <span className="link">{title}</span>
      </Link>
    </div>
  );
};

/**
 * Provide a set of react components to encapsulate the standard set of views you might need.
 * Views from this assume the standard Sidebar|Main layout for the most part.
 * Examples usages:
 *
 *      const Layout = Layout;
 *      const { Sidebar, Content } = Layout;
 *      return (
 *        <Layout>
 *          <Sidebar>
 *            <FilterPanel
 *            metric={{
 *                       type: t("ACCOUNTS"),
 *                       count: tenants.length
 *                     }}
 *            />
 *            <Sidebar.Actions>
 *              <button className="btn btn-dark">{t("CONSOLIDATE")}</button>
 *            </Sidebar.Actions>
 *          </Sidebar>
 *          <Content scrolling>
 *            <Content.Title title={t("ACCOUNTS")} />
 *            <Content.ScrollingList loading={loading}>
 *            {tenants.map((item, i) => (
 *                       <AccountCard {...item} key={i} />
 *                     ))}
 *            </Content.ScrollingList>
 *          </Content>
 *        </Layout>
 *      );
 */
export class Layout extends React.PureComponent<{
  back?: {
    to: H.LocationDescriptor | ((location: H.Location) => H.LocationDescriptor);
    persist?: Array<AllQueryParam>;
    title: string;
    iconDesc: string;
    linkDataId?: string;
  };
  title?: string;
  viewSelector?: React.ReactNode;
  className?: string;
}> {
  static Sidebar = Sidebar;
  static Content = Content;

  render() {
    const back = this.props.back ? <BackButton {...this.props.back} /> : null;
    const classes = ["layout-wrapper", this.props.className]
      .filter(c => !!c)
      .join(" ");
    const title = this.props.title ? (
      <h1 className="page-title">{this.props.title}</h1>
    ) : null;

    return (
      <>
        <div className="secondary-bar">
          <div className="left">{back}</div>
          <div className="middle">{title}</div>
          <div className="right">{this.props.viewSelector}</div>
        </div>
        <div className={classes}>{this.props.children}</div>
      </>
    );
  }
}
